'use client';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { ArrowIcon } from '../components/ui/arrow-icon';

interface PillarCardProps {
  title: string;
  description: string;
  imageSrc?: string;
  vectorSrc: string;
  bgColor: string;
  darkBgColor: string;
  vectorBgColor: string;
  imageScale?: number;
}

function PillarCard({ title, description, imageSrc, vectorSrc, bgColor, darkBgColor, vectorBgColor, imageScale = 1 }: PillarCardProps) {
  const imageSize = `${(131.41 * imageScale).toFixed(2)}%`;

  return (
    <div className="relative mx-auto h-[408px] max-h-[408px] w-full max-w-[460px]">
      {/* Bottom (larger) box — extends 1% to the left and 1% below the top box */}
      <div
        className={`absolute inset-0 rounded-12 ${darkBgColor}`}
        aria-hidden
      />
      {/* Top (smaller, original colour) box — anchored to the top-right, 99% × 99%, i.e. a ~1% inset
          on its left/bottom edges only (right/top are flush with the outer box). At the card's ~400-
          460px size that inset is ~4px, so the inner radius is the outer's (12) minus that ~4px —
          matching it exactly (as an earlier, smaller correction did) leaves the inset corner looking
          more tightly curved than the outer corner it's nested inside. */}
      <div
        className={`absolute right-0 top-0 box-border flex h-[99%] w-[99%] flex-col overflow-hidden rounded-8 ${bgColor}`}
      >
        <div className={`absolute inset-0 ${vectorBgColor}`} />
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center mix-blend-screen"
          style={{ backgroundImage: `url('${vectorSrc}')` }}
        />
        {imageSrc ? (
          <div className="pointer-events-none relative z-10 flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden p-[6%] sm:p-[7%]">
            <div className="relative" style={{ height: imageSize, width: imageSize }}>
              <Image src={imageSrc} alt="" fill className="object-contain" sizes="(max-width: 408px) 90vw, 320px" />
            </div>
          </div>
        ) : null}
        {/* Text takes the height it needs (the illustration gets the rest), so copy is never clipped.
            justify-center, not -start: now that the cards are wide enough that every description
            settles at 3 lines, the min-height floor below is taller than most cards' text actually
            needs — centering divides that slack evenly above and below instead of dumping it all
            beneath the description. */}
        <div className="relative z-20 box-border flex w-full min-w-0 shrink-0 flex-col justify-center p-[10px] min-h-[136px] sm:min-h-[156px] sm:p-[12px] md:min-h-[180px] md:p-[14px]">
          <h3 className="mb-1.5 ds-text-title-base text-gray-900 sm:mb-2 md:ds-text-title-xl">
            {title}
          </h3>
          <p className="ds-text-base-normal text-blue-bell-700">
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}

export default function SolutionSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activePillarIndex, setActivePillarIndex] = useState(0);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const slides = container.querySelectorAll<HTMLElement>('[data-pillar-slide]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const slide = entry.target as HTMLElement;
            setActivePillarIndex(Number(slide.dataset.pillarSlide));
          }
        });
      },
      { root: container, threshold: 0.5 },
    );

    slides.forEach((slide) => observer.observe(slide));
    return () => observer.disconnect();
  }, []);

  const goToPillar = (index: number): void => {
    const container = scrollRef.current;
    if (!container) return;
    const clamped = Math.max(0, Math.min(pillars.length - 1, index));
    const slide = container.querySelector<HTMLElement>(`[data-pillar-slide="${clamped}"]`);
    slide?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  const pillars: PillarCardProps[] = [
    {
      title: "Never be priced out again",
      description: "Buy tokens of real estate projects across Nigeria from just ₦10,000. No millions required. Never be priced out again from attractive projects.",
      imageSrc: "/images/pillar1.svg",
      vectorSrc: "/images/img_vector_indigo_50.png",
      bgColor: "bg-purple-100",
      darkBgColor: "bg-purple-200",
      vectorBgColor: "bg-prople-100/40"
    },
    {
      title: "Vetted deals, open to everyone",
      description: "We source and vet development projects before listing them on our platform. No private networks. No insider access required. Every project is visible to all investors.",
      imageSrc: "/images/img_group_red_50.svg",
      vectorSrc: "/images/img_vector_red_50_01.png",
      bgColor: "bg-red-100",
      darkBgColor: "bg-red-200",
      vectorBgColor: "bg-red-100/40",
      imageScale: 0.9
    },
    {
      title: "High ROI projects only",
      description: "We target projects with 15–25% projected returns. Instead of watching inflation eat into your savings, put your money into real assets that are designed to grow.",
      imageSrc: "/images/img_image_slot.png",
      vectorSrc: "/images/img_vector_light_blue_50_404x404.png",
      bgColor: "bg-cyan-100",
      darkBgColor: "bg-cyan-200",
      vectorBgColor: "bg-cyan-100/40",
      imageScale: 0.9
    },
    {
      title: "Sell whenever you want",
      description: "Your tokens also live on a secondary market. If you need your money back + profits accrued to that point — just sell your tokens to another investor.",
      imageSrc: "/images/img_image_slot_purple_50.png",
      vectorSrc: "/images/img_vector_gray_50_01_404x404.png",
      bgColor: "bg-fuchsia-100",
      darkBgColor: "bg-fuchsia-200",
      vectorBgColor: "bg-fuchsia-100/40"
    },
    {
      title: "Built on structure, not word of mouth",
      description: "Every project is held in its own dedicated entity. Valuations are triggered by independent appraisals and your stake is recorded in a water-tight legal structure.",
      imageSrc: "/images/structure-mouth.png",
      vectorSrc: "/images/img_vector_gray_100_04.png",
      bgColor: "bg-green-100",
      darkBgColor: "bg-green-200",
      vectorBgColor: "bg-green-100/40"
    },
    {
      title: "Skip the middlemen",
      description: "Browse projects, buy a stake, track your investment — all from one platform. No agents. No surveyors. No hidden fees stacked on top of your investment.",
      imageSrc: "/images/img_image_slot_gray_200_01.svg",
      vectorSrc: "/images/img_vector_gray_100_03_404x404.png",
      bgColor: "bg-blue-100",
      darkBgColor: "bg-blue-200",
      vectorBgColor: "bg-blue-100/40"
    }
  ]

  return (
    <section className="w-full px-[20px] sm:px-[40px] md:px-[49px] lg:px-[40px] mt-[49px] sm:mt-[73px] md:mt-[98px]">
      {/* Narrower side padding than other sections (98px -> 40px at lg) so the 3-column grid gets
          noticeably wider cards, giving the longer pillar descriptions enough width to settle on
          3 lines instead of 4 (per user request; not a Figma-specified measurement). */}
      <div className="w-full max-w-[621px] sm:max-w-[931px] md:max-w-[1242px] lg:max-w-[1360px] mx-auto">
        <div className="reveal-up flex flex-col gap-[6px] sm:gap-[8px] md:gap-[10px] mb-[-9px] sm:mb-[-13px] md:mb-[-18px]">
          <div className="border-l-2 border-prople-500 pl-[6px] sm:pl-[7px] md:pl-[8px]">
            <span className="ds-text-title-base text-gray-900">
              The solution
            </span>
          </div>

          <h2 className="ds-text-title-2xl sm:ds-text-title-3xl md:ds-text-title-4xl text-gray-900">
            The Proply{' '}
            <span className="relative inline-block">
              pillars
              <Image
                src="/images/line.svg"
                alt=""
                width={108}
                height={9}
                className="pointer-events-none absolute left-0 top-full mt-0.5 w-full h-auto select-none"
                aria-hidden
              />
            </span>
          </h2>
        </div>

        {/* Mobile: horizontal swipe carousel */}
        <div className="mt-[24px] sm:hidden">
          {/* scroll-pl-[20px]: the container's own px-[20px] only gives the FIRST slide a left gutter
              (it's simply the padding before the first flex item) — scrollIntoView's 'start' alignment
              and the browser's scroll-snap machinery both align to the scrollport's raw edge, so every
              slide after the first lands flush against it with no gutter at all. scroll-padding tells
              that same alignment machinery to reserve this inset for any slide, not just the first. */}
          <div
            ref={scrollRef}
            className="-mx-[20px] overflow-x-auto px-[20px] scroll-pl-[20px] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            <div className="flex snap-x snap-mandatory gap-[12px] pr-[20px]">
              {pillars.map((pillar: PillarCardProps, index: number) => (
                <div
                  key={index}
                  data-pillar-slide={index}
                  className="w-[min(408px,calc(100vw-40px))] shrink-0 snap-start"
                >
                  <PillarCard {...pillar} />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-[16px] flex items-center justify-center gap-4">
            <button
              type="button"
              aria-label="Previous pillar"
              onClick={() => goToPillar(activePillarIndex - 1)}
              disabled={activePillarIndex === 0}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-blue-bell-700 transition-opacity active:text-prople-500 disabled:opacity-30"
            >
              <ArrowIcon direction="left" />
            </button>

            <div
              className="flex items-center justify-center gap-[6px]"
              role="tablist"
              aria-label="Pillar carousel position"
            >
              {pillars.map((_, index) => (
                <span
                  key={index}
                  role="tab"
                  aria-selected={index === activePillarIndex}
                  aria-label={`Pillar ${index + 1} of ${pillars.length}`}
                  className={`h-[6px] w-[6px] rounded-full transition-[opacity,background-color] duration-[600ms] ease-in-out ${
                    index === activePillarIndex
                      ? 'bg-primary-brand opacity-100'
                      : 'bg-border-primary opacity-35'
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              aria-label="Next pillar"
              onClick={() => goToPillar(activePillarIndex + 1)}
              disabled={activePillarIndex === pillars.length - 1}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-blue-bell-700 transition-opacity active:text-prople-500 disabled:opacity-30"
            >
              <ArrowIcon direction="right" />
            </button>
          </div>
        </div>

        {/* Tablet/Desktop: grid */}
        <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-[12px] sm:gap-[16px] md:gap-[20px] mt-[34px] md:mt-[43px]">
          {pillars.map((pillar: PillarCardProps, index: number) => (
            <div key={index} className="reveal-up">
              <PillarCard {...pillar} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}